import { CatalogApi } from '@backstage/plugin-catalog-react';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { getEntityRelations } from '@backstage/plugin-catalog-react';

import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

export const getEntityRefs = (entities: Entity[], relationship: string) =>
  entities?.flatMap((entity: any) =>
    getEntityRelations(entity, relationship).map(stringifyEntityRef),
  );

const removeDuplicatedEntities = (entities: Entity[]): Entity[] => {
  const uniqueValues = new Set();
  const result = [];

  for (const entity of entities) {
    const nestedValue = entity.metadata.name;

    if (!uniqueValues.has(nestedValue)) {
      uniqueValues.add(nestedValue);
      result.push(entity);
    }
  }

  return result;
};

const getEntitiesListByRelation = async (
  catalogApi: CatalogApi,
  entity: Entity[],
  relationship: string,
) => {
  const refs = getEntityRefs(entity, relationship);
  const apiResponse = await catalogApi.getEntitiesByRefs({ entityRefs: refs });

  return apiResponse?.items as Entity[];
};

export const usingEntitiesList = async (
  catalogApi: CatalogApi,
  entity: Entity,
): Promise<Entity[]> => {
  const apis = await getEntitiesListByRelation(
    catalogApi,
    [entity],
    RELATION_CONSUMES_API,
  );
  const services = await getEntitiesListByRelation(
    catalogApi,
    apis,
    RELATION_API_PROVIDED_BY,
  );
  const dependencies = await getEntitiesListByRelation(
    catalogApi,
    services,
    RELATION_DEPENDS_ON,
  );
  const result = services.concat(dependencies);

  return removeDuplicatedEntities(result);
};

export const usedByEntitiesList = async (
  catalogApi: CatalogApi,
  entity: Entity,
): Promise<Entity[]> => {
  const apis = await getEntitiesListByRelation(
    catalogApi,
    [entity],
    RELATION_PROVIDES_API,
  );
  const services = await getEntitiesListByRelation(
    catalogApi,
    apis,
    RELATION_API_CONSUMED_BY,
  );
  const dependencies = await getEntitiesListByRelation(
    catalogApi,
    services,
    RELATION_DEPENDENCY_OF,
  );
  const result = services.concat(dependencies);

  return removeDuplicatedEntities(result);
};
